import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  CrossDayTimeWindowSchedule,
  isCrossDayTimeWindowSchedule,
} from "../types";

/**
 * Cross-Day Time Window Schedule Handler
 *
 * Handles playlists with ScheduleType.CrossDayTimeWindow (3)
 * Active during time ranges that span across multiple days.
 *
 * Example: Saturday 10:00 PM - Monday 6:00 AM (weekend overnight content)
 */
export class CrossDayTimeWindowScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.CrossDayTimeWindow;

  /**
   * Evaluate if playlist is active based on cross-day time window
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isCrossDayTimeWindowSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Invalid or missing CrossDayTimeWindow schedule configuration",
      };
    }

    const currentDay = context.currentTime.getDay(); // 0=Sunday
    const currentTime = this.getTimeString(context.currentTime);

    // Check if currently in the cross-day window
    const isInWindow = this.isInCrossDayWindow(
      currentDay,
      currentTime,
      schedule
    );

    if (!isInWindow) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Outside cross-day window (${this.getDayName(
          schedule.startDay
        )} ${schedule.startTime} - ${this.getDayName(schedule.endDay)} ${
          schedule.endTime
        })`,
        nextEvaluationTime: this.getNextWindowStart(
          schedule,
          context.currentTime
        ),
      };
    }

    // Calculate deactivation time
    const deactivationTime = this.getWindowEnd(schedule, context.currentTime);

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime,
      reason: `Active in cross-day window (${this.getDayName(
        schedule.startDay
      )} ${schedule.startTime} - ${this.getDayName(schedule.endDay)} ${
        schedule.endTime
      })`,
      nextEvaluationTime: deactivationTime,
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext
  ): Date | null {
    const schedule = playlist.schedule;

    if (!schedule || !isCrossDayTimeWindowSchedule(schedule)) {
      return null;
    }

    const currentDay = context.currentTime.getDay();
    const currentTime = this.getTimeString(context.currentTime);

    // If currently in window, return end time
    if (this.isInCrossDayWindow(currentDay, currentTime, schedule)) {
      return this.getWindowEnd(schedule, context.currentTime);
    }

    // Otherwise return next window start
    return this.getNextWindowStart(schedule, context.currentTime);
  }

  /**
   * Validate configuration
   */
  validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isCrossDayTimeWindowSchedule(schedule)) {
      errors.push(
        "CrossDayTimeWindow schedule configuration is missing or invalid"
      );
      return errors;
    }

    // Validate schedule type
    if (
      (schedule as CrossDayTimeWindowSchedule).type !==
      ScheduleType.CrossDayTimeWindow
    ) {
      errors.push(
        `Schedule type mismatch: expected ${
          ScheduleType.CrossDayTimeWindow
        }, got ${(schedule as CrossDayTimeWindowSchedule).type}`
      );
    }

    // Validate days
    if (schedule.startDay < 0 || schedule.startDay > 6) {
      errors.push("Start day must be between 0 (Sunday) and 6 (Saturday)");
    }

    if (schedule.endDay < 0 || schedule.endDay > 6) {
      errors.push("End day must be between 0 (Sunday) and 6 (Saturday)");
    }

    // Validate time format
    if (!this.isValidTimeFormat(schedule.startTime)) {
      errors.push(`Invalid start time format: ${schedule.startTime}`);
    }

    if (!this.isValidTimeFormat(schedule.endTime)) {
      errors.push(`Invalid end time format: ${schedule.endTime}`);
    }

    // Validate that it's actually a cross-day schedule
    if (
      schedule.startDay === schedule.endDay &&
      schedule.startTime >= schedule.endTime
    ) {
      errors.push(
        "For same-day schedules, use TimeWindow instead of CrossDayTimeWindow"
      );
    }

    return errors;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private isInCrossDayWindow(
    currentDay: number,
    currentTime: string,
    schedule: CrossDayTimeWindowSchedule
  ): boolean {
    const { startDay, startTime, endDay, endTime } = schedule;

    // Same day (but should use TimeWindow instead)
    if (startDay === endDay) {
      return (
        currentDay === startDay &&
        currentTime >= startTime &&
        currentTime <= endTime
      );
    }

    // Start day check
    if (currentDay === startDay && currentTime >= startTime) {
      return true;
    }

    // End day check
    if (currentDay === endDay && currentTime <= endTime) {
      return true;
    }

    // Days in between
    if (endDay > startDay) {
      // Normal range (e.g., Wed-Fri)
      return currentDay > startDay && currentDay < endDay;
    } else {
      // Wraps around week (e.g., Sat-Mon)
      return currentDay > startDay || currentDay < endDay;
    }
  }

  private getWindowEnd(
    schedule: CrossDayTimeWindowSchedule,
    currentTime: Date
  ): Date {
    const currentDay = currentTime.getDay();
    const { endDay, endTime } = schedule;

    // Calculate days until end day
    let daysUntil = endDay - currentDay;
    if (daysUntil < 0) {
      daysUntil += 7;
    }

    const endDate = new Date(currentTime);
    endDate.setDate(endDate.getDate() + daysUntil);

    const [hours, minutes, seconds] = endTime.split(":").map(Number);
    endDate.setHours(hours, minutes, seconds || 0, 0);

    return endDate;
  }

  private getNextWindowStart(
    schedule: CrossDayTimeWindowSchedule,
    currentTime: Date
  ): Date {
    const currentDay = currentTime.getDay();
    const currentTimeStr = this.getTimeString(currentTime);
    const { startDay, startTime } = schedule;

    // If on start day but before start time
    if (currentDay === startDay && currentTimeStr < startTime) {
      const nextStart = new Date(currentTime);
      const [hours, minutes, seconds] = startTime.split(":").map(Number);
      nextStart.setHours(hours, minutes, seconds || 0, 0);
      return nextStart;
    }

    // Calculate days until start day
    let daysUntil = startDay - currentDay;
    if (daysUntil <= 0) {
      daysUntil += 7;
    }

    const nextStart = new Date(currentTime);
    nextStart.setDate(nextStart.getDate() + daysUntil);

    const [hours, minutes, seconds] = startTime.split(":").map(Number);
    nextStart.setHours(hours, minutes, seconds || 0, 0);

    return nextStart;
  }

  private getTimeString(date: Date): string {
    return date.toTimeString().split(" ")[0]; // "HH:MM:SS"
  }

  private isValidTimeFormat(timeStr: string): boolean {
    if (!timeStr) return false;
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
    return timeRegex.test(timeStr);
  }

  private getDayName(day: number): string {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[day] || "Unknown";
  }
}
